import { useFormContext, get } from "react-hook-form";
import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";

const formVariants = cva(
    "sm:text-md/6 block field-sizing-content min-h-30 w-full min-w-0 rounded-3xl bg-white py-3 text-base outline-2 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:opacity-50",
    {
        variants: {
            variant: {
                primary:
                    "text-darkgreen-500 shadow-darkgreen-200/25 outline-neutral-200 placeholder:text-darkgreen-500/75 focus:outline-darkgreen-500/50",
                success:
                    "text-lime-700 shadow-lime-500/25 outline-lime-500 placeholder:text-lime-700/75 focus:outline-lime-600",
                danger: "text-red-400 shadow-red-500/25 outline-red-500 placeholder:text-red-400/75 focus:outline-red-600",
            },
            size: {
                default: "font-base rounded-3xl px-6 py-3 text-base",
                sm: "font-sm rounded-3xl px-4 py-2 text-sm",
                lg: "font-lg rounded-3xl px-8 py-4 text-lg",
            },
            iconPosition: {
                left: "pl-12",
                right: "pr-12",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            iconPosition: "none",
        },
    },
);

const labelVariants = cva("mb-1 block w-full text-sm/6 font-medium", {
    variants: {
        variant: {
            primary: "text-darkgreen-500",
            success: "text-lime-700",
            danger: "text-red-500",
        },
    },
    compoundVariants: [
        {
            variant: ["primary", "success", "danger"],
            hideLabel: true,
            className: "sr-only w-fit",
        },
    ],
    defaultVariants: {
        variant: "primary",
        hideLabel: false,
    },
});

export const Textarea = ({
    label,
    type,
    name,
    placeholder,
    value,
    className,
    variant,
    size,
    disabled,
    hideLabel,
    ...props
}) => {
    const {
        register,
        formState: { errors /*isSubmitted*/ },
    } = useFormContext();

    const hasError = get(errors, name);
    const formStateVariant = hasError
        ? "danger"
        : /*: isSubmitted
          ? "success"*/
          variant || "primary";

    return (
        <>
            <label
                htmlFor={name}
                className={cn(
                    labelVariants({
                        variant: formStateVariant,
                        hideLabel,
                    }),
                )}
            >
                {label}
            </label>
            <div className="relative flex">
                <textarea
                    id={name}
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    className={cn(
                        formVariants({
                            className,
                            variant: formStateVariant,
                            size,
                            type,
                        }),
                    )}
                    {...register(name)}
                    aria-invalid={hasError ? "true" : "false"}
                    {...props}
                />
            </div>
            {hasError && (
                <div className="mt-2">
                    <TextareaError
                        message={hasError.message}
                        key={hasError.message}
                    />
                </div>
            )}
        </>
    );
};

const TextareaError = ({ message }) => {
    return (
        <div className="px-4">
            <span role="alert" className="text-red-400">
                {message}
            </span>
        </div>
    );
};
